import React from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard,
    MessagesSquare,
    History as HistoryIcon,
    ShieldAlert,
    Send,
    Puzzle,
    LogOut,
    Flame,
    Users,
    CreditCard,
    Shield,
    Target,
    FolderOpen,
} from "lucide-react";

const navItems = [
    { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
    { to: "/app/clients", icon: Users, label: "Clients", testid: "nav-clients" },
    { to: "/app/outreach", icon: Target, label: "Cold Outreach", testid: "nav-outreach" },
    { to: "/app/new", icon: MessagesSquare, label: "New Reply", testid: "nav-new" },
    { to: "/app/library", icon: FolderOpen, label: "Product Library", testid: "nav-library" },
    { to: "/app/history", icon: HistoryIcon, label: "History", testid: "nav-history" },
    { to: "/app/objections", icon: ShieldAlert, label: "Objections", testid: "nav-objections" },
    { to: "/app/followups", icon: Send, label: "Follow-ups", testid: "nav-followups" },
    { to: "/app/team", icon: Users, label: "Team", testid: "nav-team" },
    { to: "/app/billing", icon: CreditCard, label: "Billing", testid: "nav-billing" },
    { to: "/app/extension", icon: Puzzle, label: "Extension", testid: "nav-extension" },
];

const adminNavItem = { to: "/app/admin", icon: Shield, label: "Admin", testid: "nav-admin" };

export default function AppShell() {
    const { user, logout } = useAuth();

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 flex-col border-r border-zinc-900 bg-[#0a0a0a] sticky top-0 h-screen">
                <Link to="/" className="px-6 py-6 flex items-center gap-2 border-b border-zinc-900" data-testid="sidebar-logo">
                    <Flame className="w-5 h-5 text-signal" strokeWidth={2} />
                    <span className="font-heading font-black text-lg tracking-tight">
                        sales<span className="text-signal">whisper</span>
                    </span>
                </Link>
                <nav className="flex-1 py-4 px-3 space-y-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                    isActive
                                        ? "bg-zinc-900 text-white border-l-2 border-signal"
                                        : "text-zinc-400 hover:text-white hover:bg-zinc-900/60"
                                }`
                            }
                        >
                            <item.icon className="w-4 h-4" strokeWidth={1.5} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                    {user?.is_admin && (
                        <NavLink
                            to={adminNavItem.to}
                            data-testid={adminNavItem.testid}
                            className={({ isActive }) =>
                                `mt-2 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors border-t border-zinc-900 pt-4 ${
                                    isActive
                                        ? "bg-zinc-900 text-white border-l-2 border-signal"
                                        : "text-signal hover:text-white hover:bg-zinc-900/60"
                                }`
                            }
                        >
                            <adminNavItem.icon className="w-4 h-4" strokeWidth={1.5} />
                            <span>{adminNavItem.label}</span>
                        </NavLink>
                    )}
                </nav>
                <div className="px-3 pb-4 border-t border-zinc-900 pt-4">
                    <div className="px-3 mb-3">
                        <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Operator</div>
                        <div className="text-sm text-white mt-1 truncate">{user?.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={logout}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-zinc-900"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top-bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-zinc-900 flex items-center justify-between px-4 h-14">
                <Link to="/app" className="flex items-center gap-2" data-testid="mobile-logo">
                    <Flame className="w-4 h-4 text-signal" />
                    <span className="font-heading font-black">saleswhisper</span>
                </Link>
                <button onClick={logout} className="text-xs text-zinc-400" data-testid="mobile-logout">Sign out</button>
            </div>

            <main className="flex-1 min-w-0 md:pl-0 pt-14 md:pt-0">
                <div className="px-6 sm:px-8 md:px-10 py-8 max-w-7xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
